import { useLocation } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { DATA_LAST_UPDATED, DATA_SOURCES } from '../utils/constants.ts'

const globalDisclaimer =
  'This calculator provides estimates for informational purposes only. Results are based on aggregated labor market data (PwC, BLS, O*NET) and are not a guarantee of individual earnings, savings, or outcomes. This is not financial, legal, tax, or career advice. Consult a qualified professional before making decisions based on these estimates.'

const specificDisclaimers: Record<string, string[]> = {
  '/ai-tax-deductions': [
    'Consult a CPA or tax advisor. Deductibility depends on your specific situation and IRS/state rules.'
  ],
  '/displacement-risk': [
    'Risk scores reflect statistical trends, not individual job security.'
  ],
  '/hire-vs-ai': [
    'Labor law and compliance requirements vary by jurisdiction.'
  ],
  '/ai-legal-cost': [
    'Not a substitute for licensed legal counsel.'
  ],
  '/ai-consulting-rate': [
    'Market rates vary significantly by geography, client, and specialization.'
  ],
  '/ai-freelance-rate': [
    'Market rates vary significantly by geography, client, and specialization.'
  ],
  '/lifetime-earnings': [
    'Projections assume constant conditions and are illustrative only. Actual results will vary.',
    'Projections beyond 5 years carry significant uncertainty. AI labor markets are evolving rapidly.'
  ],
  '/ai-copyright-ip-risk': [
    'Not legal advice. IP risk depends on specific facts, jurisdiction, and evolving case law. Consult a licensed attorney.'
  ],
  '/employee-ai-reimbursement': [
    'Tax and reimbursement rules vary by employer and jurisdiction. Confirm with your payroll or tax advisor.'
  ]
}

const calculatorPaths = new Set([
  '/ai-salary-gap',
  '/skills-roi',
  '/ai-productivity',
  '/hire-vs-ai',
  '/automation-savings',
  '/displacement-risk',
  '/lifetime-earnings',
  '/ai-tax-deductions',
  '/ai-freelance-rate',
  '/ai-subscription-roi',
  '/ai-raise-calculator',
  '/ai-side-hustle',
  '/ai-api-cost',
  '/ai-vs-offshore',
  '/ai-training-roi',
  '/ai-content-roi',
  '/ai-consulting-rate',
  '/ai-revenue-impact',
  '/ai-job-search',
  '/ai-pricing-elasticity',
  '/ai-compliance-cost',
  '/ai-team-sizing',
  '/ai-error-cost',
  '/ai-support-roi',
  '/ai-tool-budget',
  '/ai-healthcare-admin',
  '/ai-portfolio-exposure',
  '/ai-legal-cost',
  '/ai-recruiting-roi',
  '/ai-ad-creative-roi',
  '/ai-carbon-cost',
  '/ai-coding-productivity',
  '/local-vs-cloud-ai',
  '/rag-vs-fine-tuning',
  '/data-readiness-cost',
  '/multi-agent-workflow',
  '/ai-voice-video',
  '/saas-vs-custom-agent',
  '/lead-response-roi',
  '/data-degradation-saver',
  '/ai-cyber-risk',
  '/ai-career-transition',
  '/rto-vs-ai-nomad',
  '/ai-copyright-ip-risk',
  '/ai-hardware-upgrade-roi',
  '/ai-gig-arbitrage',
  '/employee-ai-reimbursement'
])

export default function LegalDisclaimer() {
  const location = useLocation()
  const path = location.pathname.replace(/\/$/, '') || '/'
  if (!calculatorPaths.has(path)) return null

  const extra = specificDisclaimers[path] || []

  return (
    <section className="bg-slate-50 border-t border-slate-200 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900 mb-1">Important disclaimer</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{globalDisclaimer}</p>
              {extra.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {extra.map((item, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-amber-500 mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-slate-500">
                Salary and premium data last updated: {DATA_LAST_UPDATED}. Sources: {DATA_SOURCES.join(', ')}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
